/**
 * sentry.ts — Sentry error monitoring para RHEA (Sprint 11)
 * ─────────────────────────────────────────────────────────────────────────────
 * Inicializa Sentry React SDK usando VITE_SENTRY_DSN.
 * Sin DSN → no-op silencioso (dev / staging sin config).
 *
 * Características activadas:
 *   - Captura automática de errores no manejados (window.onerror)
 *   - Captura de promesas rechazadas (unhandledrejection)
 *   - Breadcrumbs de consola y navegación
 *   - Session Replay desactivado (privacidad, sin costo adicional)
 *
 * Uso en main.tsx:
 *   import { initSentry } from "@/lib/sentry";
 *   initSentry();
 *   // Luego envolver RouterProvider con <Sentry.ErrorBoundary>
 *
 * Variables de entorno requeridas en Vercel:
 *   VITE_SENTRY_DSN=https://xxxxx@o0.ingest.sentry.io/xxxxx
 *
 * Para obtener tu DSN:
 *   sentry.io → New Project → React → copiar el DSN
 */

import * as Sentry from "@sentry/react";

const DSN  = import.meta.env.VITE_SENTRY_DSN  as string | undefined;
const ENV  = import.meta.env.VITE_RHEA_ENV    as string | undefined ?? "development";
const PROD = ENV === "production";

// Función de inicialización — llamar UNA VEZ en main.tsx antes del render
export function initSentry(): void {
  if (!DSN) return; // sin DSN → no-op silencioso

  Sentry.init({
    dsn: DSN,
    environment: ENV,

    // Sólo enviar errores en producción; en staging capturamos pero con menor volumen
    sampleRate:        PROD ? 1.0 : 0.25,
    tracesSampleRate:  PROD ? 0.10 : 0.0,  // 10% de traces en prod, 0 en dev/staging
    replaysOnErrorSampleRate: 0,            // Sin session replay (privacidad)
    replaysSessionSampleRate: 0,

    // Ignorar errores de red que no son bugs nuestros
    ignoreErrors: [
      "NetworkError",
      "Failed to fetch",
      "Load failed",
      "AbortError",
      "ResizeObserver loop",
      /^Script error/,
    ],

    // Fingerprinting manual para agrupar errores del backend de forma útil
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeSend(event: any) {
      // No enviar eventos de desarrollo local
      if (typeof window !== "undefined" && window.location.hostname === "localhost") {
        return null;
      }
      return event;
    },
  });
}

// Re-exportar ErrorBoundary de Sentry para uso en main.tsx
export { Sentry };
